import React from 'react';
import { PLANS, STORE_COLORS, addDays, makeDate } from './data.jsx';
import { Icon } from './icons.jsx';
import { configService, formatTenantCreateError, planService, tenantService } from './services.jsx';
import { nextDomainFromNameChange } from '../src/tenant-domain-slug.js';
// ── Field helpers — defined OUTSIDE component to prevent re-mount on render ──
const NSP_INP  = { width:'100%', height:38, padding:'0 12px', borderRadius:8, border:'1px solid var(--border-strong)', background:'var(--bg-elevated)', color:'var(--text)', fontSize:13, fontFamily:'inherit' };
const NSP_INPE = { ...NSP_INP, borderColor:'var(--brand-red)' };

const NSField = ({ label, req, type, placeholder, hint, span2, value, error, onChange }) => (
  <div style={span2 ? { gridColumn:'1/-1' } : {}}>
    <div style={{ fontSize:12, fontWeight:500, color:'var(--text-secondary)', marginBottom:5 }}>
      {label}{req && <span style={{ color:'var(--brand-red)', marginLeft:2 }}>*</span>}
    </div>
    <input type={type||'text'} style={error ? NSP_INPE : NSP_INP} value={value||''} onChange={onChange} placeholder={placeholder} />
    {error && <div style={{ fontSize:11, color:'var(--brand-red)', marginTop:3 }}>{error}</div>}
    {hint && !error && <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:3 }}>{hint}</div>}
  </div>
);

const NSG2 = ({ children }) => <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>{children}</div>;
const NSG3 = ({ children }) => <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:14 }}>{children}</div>;
const NSSectionHead = ({ title }) => (
  <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:14, paddingBottom:8, borderBottom:'1px solid var(--border)' }}>{title}</div>
);

// Página single de criação de novo tenant — POST /api/v1/super-admin/tenants/
const NewStorePage = ({ onNav, onSave }) => {
  const STEPS = [
    { id: 1, label: 'Identificação',        desc: 'Nome, domínio e plano'          },
    { id: 2, label: 'Acesso admin',          desc: 'E-mail e senha inicial'          },
    { id: 3, label: 'Configurações da loja', desc: 'CNPJ, contato e endereço'        },
    { id: 4, label: 'Centro de distribuição',desc: 'Logística e operação'            },
    { id: 5, label: 'Revisão',               desc: 'Confirme e crie o tenant'        },
  ];

  const empty = {
    name:'', fiscal_name:'', domain:'', plan:'basic',
    email:'', password:'', confirmPass:'',
    cnpj:'', phone:'', contact_email:'',
    street:'', number:'', neighborhood:'', city:'', state:'SP', postal_code:'',
    notification_sender_email:'', notification_sender_name:'',
    dc_name:'', dc_email:'', dc_phone:'', dc_type:'shipping_and_pickup',
    dc_street:'', dc_number:'', dc_complement:'', dc_neighborhood:'',
    dc_city:'', dc_state:'SP', dc_postcode:'',
  };

  const [form,    setForm]    = React.useState(empty);
  const [errors,  setErrors]  = React.useState({});
  const [step,    setStep]    = React.useState(1);
  const [loading,   setLoading]   = React.useState(false);
  const [saveError,  setSaveError]  = React.useState(null);
  const [domainConfig, setDomainConfig] = React.useState(null);
  const [domainConfigError, setDomainConfigError] = React.useState(null);

  React.useEffect(() => {
    let mounted = true;
    if (typeof configService === 'undefined') {
      setDomainConfigError('Configuração de domínio indisponível.');
      return () => { mounted = false; };
    }
    configService.getConfig().then((cfg) => {
      if (!mounted) return;
      setDomainConfig(cfg);
      setDomainConfigError(cfg?._fallback ? 'Usando domínio padrão local — não foi possível carregar a config do servidor.' : null);
    });
    return () => { mounted = false; };
  }, []);

  React.useEffect(() => {
    if (typeof planService !== 'undefined') {
      planService.listPlans().catch(() => {});
    }
  }, []);

  const domainSuffix = typeof configService !== 'undefined'
    ? configService.domainSuffixFromConfig(domainConfig)
    : '.localhost';
  const domainPreview = (form.domain && !errors.domain && typeof configService !== 'undefined')
    ? configService.buildTenantUrl(configService.buildDomainFromSubdomain(form.domain, domainConfig), domainConfig)
    : null;

  const set = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(f => ({ ...f, [k]: v }));
    if (errors[k]) setErrors(er => ({ ...er, [k]: null }));
  };

  const handleNameChange = (e) => {
    const val = e.target.value;
    setForm(f => ({
      ...f,
      name: val,
      domain: nextDomainFromNameChange({
        previousName: f.name,
        previousDomain: f.domain,
        nextName: val,
      }),
    }));
    if (errors.name) setErrors(er => ({ ...er, name: null }));
  };

  const validate = (s) => {
    const e = {};
    if (s===1) {
      if (!form.name.trim())        e.name        = 'Obrigatório';
      if (!form.fiscal_name.trim()) e.fiscal_name = 'Obrigatório';
      if (!form.domain.trim())      e.domain      = 'Obrigatório';
      else if (!/^[a-z0-9-]+$/.test(form.domain)) e.domain = 'Apenas letras minúsculas, números e hífens';
    }
    if (s===2) {
      if (!form.email.trim()) e.email = 'Obrigatório';
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'E-mail inválido';
      if (!form.password) e.password = 'Obrigatório';
      else if (form.password.length < 8) e.password = 'Mínimo 8 caracteres';
      if (form.password !== form.confirmPass) e.confirmPass = 'Senhas não coincidem';
    }
    if (s===3) {
      if (!form.cnpj.trim())          e.cnpj          = 'Obrigatório';
      if (!form.phone.trim())         e.phone         = 'Obrigatório';
      if (!form.contact_email.trim()) e.contact_email = 'Obrigatório';
      if (!form.street.trim())        e.street        = 'Obrigatório';
      if (!form.city.trim())          e.city          = 'Obrigatório';
      if (!form.postal_code.trim())   e.postal_code   = 'Obrigatório';
    }
    if (s===4) {
      if (!form.dc_name.trim())   e.dc_name   = 'Obrigatório';
      if (!form.dc_email.trim())  e.dc_email  = 'Obrigatório';
      if (!form.dc_street.trim()) e.dc_street = 'Obrigatório';
      if (!form.dc_city.trim())   e.dc_city   = 'Obrigatório';
    }
    return e;
  };

  const handleNext = () => {
    const e = validate(step);
    if (Object.keys(e).length) { setErrors(e); return; }
    setStep(s => s + 1);
  };

  const handleCreate = async () => {
    setLoading(true);
    setSaveError(null);
    const payload = {
      name: form.name, fiscal_name: form.fiscal_name,
      email: form.email, password: form.password,
      subdomain: form.domain, plan: form.plan,
      store_settings: {
        name: form.name, cnpj: form.cnpj, phone: form.phone,
        contact_email: form.contact_email,
        street: form.street, number: form.number,
        neighborhood: form.neighborhood, city: form.city,
        state: form.state, postal_code: form.postal_code,
        notification_sender_email: form.notification_sender_email || form.email,
        notification_sender_name:  form.notification_sender_name  || form.name,
      },
      distribution_center: {
        name: form.dc_name, email: form.dc_email,
        phone_number: form.dc_phone, type_operation: form.dc_type,
        street: form.dc_street, number: form.dc_number,
        complement: form.dc_complement, neighborhood: form.dc_neighborhood,
        city: form.dc_city, state: form.dc_state, postcode: form.dc_postcode,
      },
    };
    try {
      // Real backend endpoint: POST /api/v1/super-admin/tenants/
      const result = await tenantService.create(payload);
      const plan = (PLANS || []).find(p => p.id === form.plan) || (PLANS || [])[0] || { id:'basic', name:'Basic', price:199 };
      const newStore = {
        id: result.tenant_id, tenant_id: result.tenant_id,
        schema_name: result.schema_name,
        name: form.name,
        domain: result.domain || (domainConfig ? configService.buildDomainFromSubdomain(form.domain, domainConfig) : null),
        customDomain: null, plan, status: 'trial', billing_status: 'trialing',
        createdAt: makeDate(0), nextDue: addDays(makeDate(0), 14),
        color: STORE_COLORS[Math.floor(Math.random()*STORE_COLORS.length)],
        mrr: 0, ownerName: form.name, ownerEmail: form.email, ownerPhone: form.phone,
        city: form.city, state: form.state, cnpj: form.cnpj,
        usage: { pageviews:0, users:1 },
      };
      if (onSave) onSave(newStore);
      else if (onNav) onNav({ page: 'loja', store: newStore });
    } catch (e) {
      const msg = typeof formatTenantCreateError === 'function'
        ? formatTenantCreateError(e)
        : (e.message || 'Erro inesperado ao criar tenant.');
      setSaveError(msg);
    } finally {
      setLoading(false);
    }
  };


  const DC_TYPES = [
    { id:'shipping_and_pickup', label:'Envio + Retirada' },
    { id:'shipping_only',       label:'Somente envio'   },
    { id:'pickup_only',         label:'Somente retirada'},
  ];

  const plan = PLANS.find(p => p.id === form.plan) || PLANS[0];

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="content" style={{ maxWidth:1100 }}>
      {/* Breadcrumb */}
      <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:13, color:'var(--text-muted)', marginBottom:20 }}>
        <a onClick={() => onNav({ page:'lojas' })} style={{ color:'var(--text-muted)', cursor:'pointer', textDecoration:'none' }}
          onMouseEnter={e => e.currentTarget.style.color='var(--text)'}
          onMouseLeave={e => e.currentTarget.style.color='var(--text-muted)'}>
          Lojas
        </a>
        <Icon name="chevronRight" size={11} />
        <span style={{ color:'var(--text)', fontWeight:600 }}>Novo tenant</span>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'240px 1fr', gap:32, alignItems:'start' }}>

        {/* ── SIDEBAR DE PASSOS ──────────────────────────────────────────── */}
        <div style={{ position:'sticky', top:'calc(var(--topbar-h) + 20px)' }}>
          <div style={{ marginBottom:20 }}>
            <h1 style={{ fontSize:20, fontWeight:700, marginBottom:4 }}>Criar tenant</h1>
            <div style={{ fontSize:13, color:'var(--text-muted)', fontFamily:'JetBrains Mono, monospace' }}>
              POST /api/v1/super-admin/tenants/
            </div>
          </div>

          <div style={{ display:'flex', flexDirection:'column', gap:2 }}>
            {STEPS.map(s => {
              const done    = step > s.id;
              const current = step === s.id;
              return (
                <div key={s.id} onClick={done ? () => setStep(s.id) : undefined} style={{
                  padding:'10px 12px', borderRadius:10, cursor: done ? 'pointer' : 'default',
                  background: current ? 'color-mix(in srgb, var(--brand-navy) 6%, var(--bg-elevated))' : 'transparent',
                  border: current ? '1px solid color-mix(in srgb, var(--brand-navy) 20%, transparent)' : '1px solid transparent',
                  display:'flex', alignItems:'center', gap:10,
                  transition:'background 120ms',
                }}>
                  <div style={{
                    width:24, height:24, borderRadius:'50%', flexShrink:0,
                    display:'flex', alignItems:'center', justifyContent:'center',
                    fontSize:11, fontWeight:700,
                    background: done ? 'var(--success)' : current ? 'var(--brand-navy)' : 'var(--bg-subtle)',
                    color: done || current ? 'white' : 'var(--text-muted)',
                  }}>
                    {done ? <Icon name="check" size={12} /> : s.id}
                  </div>
                  <div>
                    <div style={{ fontSize:13, fontWeight: current ? 600 : 500, color: current ? 'var(--text)' : done ? 'var(--text-secondary)' : 'var(--text-muted)' }}>{s.label}</div>
                    <div style={{ fontSize:11, color:'var(--text-muted)' }}>{s.desc}</div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          <div style={{ marginTop:20, height:4, borderRadius:2, background:'var(--border)' }}>
            <div style={{ height:'100%', borderRadius:2, background:'var(--brand-navy)', width:`${(step/STEPS.length)*100}%`, transition:'width 300ms' }} />
          </div>
          <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>
            {step === STEPS.length ? 'Pronto para criar' : `${STEPS.length - step} passo${STEPS.length - step !== 1 ? 's' : ''} restante${STEPS.length - step !== 1 ? 's' : ''}`}
          </div>
        </div>

        {/* ── CONTEÚDO DO PASSO ──────────────────────────────────────────── */}
        <div>
          <div className="card" style={{ padding:28, minHeight:480 }}>

            {/* STEP 1 */}
            {step === 1 && (
              <>
                <NSSectionHead title="Identidade do tenant" />
                <div style={{ display:'flex', flexDirection:'column', gap:16, marginBottom:28 }}>
                  <NSG2>
                    <div>
                      <div style={{ fontSize:12, fontWeight:500, color:'var(--text-secondary)', marginBottom:5 }}>Nome fantasia<span style={{ color:'var(--brand-red)', marginLeft:2 }}>*</span></div>
                      <input style={errors.name ? NSP_INPE : NSP_INP} value={form.name} onChange={handleNameChange} placeholder="Ex: CasaMax" />
                      {errors.name && <div style={{ fontSize:11, color:'var(--brand-red)', marginTop:3 }}>{errors.name}</div>}
                    </div>
                    <div>
                      <div style={{ fontSize:12, fontWeight:500, color:'var(--text-secondary)', marginBottom:5 }}>Razão social<span style={{ color:'var(--brand-red)', marginLeft:2 }}>*</span></div>
                      <input style={errors.fiscal_name ? NSP_INPE : NSP_INP} value={form.fiscal_name} onChange={set('fiscal_name')} placeholder="CasaMax Comércio Ltda" />
                      {errors.fiscal_name && <div style={{ fontSize:11, color:'var(--brand-red)', marginTop:3 }}>{errors.fiscal_name}</div>}
                    </div>
                  </NSG2>
                  <div>
                    <div style={{ fontSize:12, fontWeight:500, color:'var(--text-secondary)', marginBottom:5 }}>Domínio<span style={{ color:'var(--brand-red)', marginLeft:2 }}>*</span></div>
                    <div style={{ display:'flex' }}>
                      <input style={{ ...(errors.domain ? NSP_INPE : NSP_INP), borderRadius:'8px 0 0 8px', flex:1 }} value={form.domain} onChange={set('domain')} placeholder="casamax" />
                      <div style={{ height:38, padding:'0 14px', background:'var(--bg-subtle)', border:'1px solid var(--border-strong)', borderLeft:'none', borderRadius:'0 8px 8px 0', display:'flex', alignItems:'center', fontSize:13, color:'var(--text-muted)', whiteSpace:'nowrap', fontFamily:'JetBrains Mono, monospace' }}>{domainSuffix}</div>
                    </div>
                    {errors.domain && <div style={{ fontSize:11, color:'var(--brand-red)', marginTop:3 }}>{errors.domain}</div>}
                    {domainConfigError && <div style={{ fontSize:11, color:'var(--warning)', marginTop:3 }}>{domainConfigError}</div>}
                    {domainPreview && <div style={{ fontSize:12, color:'var(--text-muted)', marginTop:4, fontFamily:'JetBrains Mono, monospace' }}>↳ {domainPreview}</div>}
                  </div>
                </div>

                <NSSectionHead title="Plano de assinatura" />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  {PLANS.map(p => {
                    const sel = form.plan === p.id;
                    return (
                      <div key={p.id} onClick={() => setForm(f => ({ ...f, plan:p.id }))} style={{
                        padding:16, borderRadius:12, cursor:'pointer',
                        border: sel ? '2px solid var(--brand-navy)' : '1px solid var(--border)',
                        background: sel ? 'color-mix(in srgb, var(--brand-navy) 4%, var(--bg-elevated))' : 'var(--bg-subtle)',
                      }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                          <div style={{ fontWeight:700, fontSize:15 }}>{p.name}</div>
                          <div style={{ fontWeight:700, fontSize:15, color: sel ? 'var(--brand-navy)' : 'var(--text)' }}>R$ {p.price}<span style={{ fontSize:11, fontWeight:400, color:'var(--text-muted)' }}>/mês</span></div>
                        </div>
                        <div style={{ fontSize:12, color:'var(--text-muted)', display:'flex', flexDirection:'column', gap:2 }}>
                          <span>{p.pageviews.toLocaleString('pt-BR')} pageviews</span>
                          <span>{p.pageviews.toLocaleString('pt-BR')} pageviews · {p.users} usuários</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}

            {/* STEP 2 */}
            {step === 2 && (
              <>
                <NSSectionHead title="Credenciais do administrador da loja" />
                <div style={{ display:'flex', flexDirection:'column', gap:16, maxWidth:480 }}>
                  <NSField label="E-mail do admin"  req type="email" placeholder="admin@casamax.com.br" hint="Usado para login no painel da loja e envio de faturas." value={form.email} onChange={set('email')} error={errors.email} />
                  <div>
                    <div style={{ fontSize:12, fontWeight:500, color:'var(--text-secondary)', marginBottom:5 }}>Senha inicial<span style={{ color:'var(--brand-red)', marginLeft:2 }}>*</span></div>
                    <input type="password" style={errors.password ? NSP_INPE : NSP_INP} value={form.password} onChange={set('password')} placeholder="Mínimo 8 caracteres" />
                    {errors.password && <div style={{ fontSize:11, color:'var(--brand-red)', marginTop:3 }}>{errors.password}</div>}
                    {!errors.password && <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:3 }}>Use letras maiúsculas, números e símbolos.</div>}
                  </div>
                  <div>
                    <div style={{ fontSize:12, fontWeight:500, color:'var(--text-secondary)', marginBottom:5 }}>Confirmar senha<span style={{ color:'var(--brand-red)', marginLeft:2 }}>*</span></div>
                    <input type="password" style={errors.confirmPass ? NSP_INPE : NSP_INP} value={form.confirmPass} onChange={set('confirmPass')} placeholder="Repita a senha" />
                    {errors.confirmPass && <div style={{ fontSize:11, color:'var(--brand-red)', marginTop:3 }}>{errors.confirmPass}</div>}
                  </div>
                  <div style={{ background:'var(--warning-bg)', color:'var(--warning)', borderRadius:8, padding:'10px 14px', fontSize:13, display:'flex', gap:10 }}>
                    <Icon name="alert" size={15} style={{ flexShrink:0, marginTop:1 }} />
                    A senha será enviada ao lojista por e-mail. Oriente-o a alterá-la no primeiro acesso.
                  </div>
                </div>
              </>
            )}

            {/* STEP 3 */}
            {step === 3 && (
              <>
                <NSSectionHead title="Dados cadastrais" />
                <NSG3 style={{ marginBottom:20 }}>
                  <NSField label="CNPJ"  req placeholder="12.345.678/0001-90" value={form.cnpj} onChange={set('cnpj')} error={errors.cnpj} />
                  <NSField label="Telefone"  req placeholder="(11) 4002-8922" value={form.phone} onChange={set('phone')} error={errors.phone} />
                  <div style={{ gridColumn:'3' }}>
                    <NSField label="E-mail de contato"  req type="email" placeholder="atendimento@casamax.com.br" value={form.contact_email} onChange={set('contact_email')} error={errors.contact_email} />
                  </div>
                </NSG3>

                <NSSectionHead title="Endereço da loja" />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 80px', gap:14, marginBottom:20 }}>
                  <NSField label="CEP"  req placeholder="88350-000" value={form.postal_code} onChange={set('postal_code')} error={errors.postal_code} />
                  <NSField label="Logradouro"  req placeholder="Avenida das Nações" value={form.street} onChange={set('street')} error={errors.street} />
                  <NSField label="Nº"  placeholder="1500" value={form.number} onChange={set('number')} error={errors.number} />
                  <NSField label="Bairro"  placeholder="Centro" value={form.neighborhood} onChange={set('neighborhood')} error={errors.neighborhood} />
                  <NSField label="Cidade"  req placeholder="Brusque" value={form.city} onChange={set('city')} error={errors.city} />
                  <NSField label="UF"  placeholder="SC" value={form.state} onChange={set('state')} error={errors.state} />
                </div>

                <NSSectionHead title="Notificações" />
                <NSG2>
                  <NSField label="E-mail remetente"  type="email" placeholder="notificacoes@casamax.com.br" hint={`Padrão: ${form.email || 'e-mail admin'}`} value={form.notification_sender_email} onChange={set('notification_sender_email')} error={errors.notification_sender_email} />
                  <NSField label="Nome remetente"  placeholder="CasaMax Notificações" hint={`Padrão: ${form.name || 'nome fantasia'}`} value={form.notification_sender_name} onChange={set('notification_sender_name')} error={errors.notification_sender_name} />
                </NSG2>
              </>
            )}

            {/* STEP 4 */}
            {step === 4 && (
              <>
                <NSSectionHead title="Centro de distribuição" />
                <NSG2 style={{ marginBottom:20 }}>
                  <div style={{ gridColumn:'1/-1' }}>
                    <NSField label="Nome do CD"  req placeholder="CasaMax CD Matriz" span2 value={form.dc_name} onChange={set('dc_name')} error={errors.dc_name} />
                  </div>
                  <NSField label="E-mail"  req type="email" placeholder="logistica@casamax.com.br" value={form.dc_email} onChange={set('dc_email')} error={errors.dc_email} />
                  <NSField label="Telefone"  placeholder="(11) 4002-8922" value={form.dc_phone} onChange={set('dc_phone')} error={errors.dc_phone} />
                  <div style={{ gridColumn:'1/-1' }}>
                    <div style={{ fontSize:12, fontWeight:500, color:'var(--text-secondary)', marginBottom:8 }}>Tipo de operação<span style={{ color:'var(--brand-red)', marginLeft:2 }}>*</span></div>
                    <div style={{ display:'flex', gap:10 }}>
                      {[
                        { id:'shipping_and_pickup', label:'Envio + Retirada', desc:'Permite ambos os fluxos'     },
                        { id:'shipping_only',       label:'Somente envio',    desc:'Não aceita retirada'          },
                        { id:'pickup_only',         label:'Somente retirada', desc:'Não faz envio para clientes'  },
                      ].map(t => {
                        const sel = form.dc_type === t.id;
                        return (
                          <div key={t.id} onClick={() => setForm(f => ({ ...f, dc_type:t.id }))} style={{
                            flex:1, padding:'10px 14px', borderRadius:10, cursor:'pointer',
                            border: sel ? '2px solid var(--brand-navy)' : '1px solid var(--border)',
                            background: sel ? 'color-mix(in srgb, var(--brand-navy) 4%, var(--bg-elevated))' : 'var(--bg-subtle)',
                          }}>
                            <div style={{ fontWeight:600, fontSize:13, color: sel ? 'var(--brand-navy)' : 'var(--text)' }}>{t.label}</div>
                            <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:2 }}>{t.desc}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </NSG2>

                <NSSectionHead title="Endereço do CD" />
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 80px', gap:14 }}>
                  <NSField label="CEP"  placeholder="88355-400" value={form.dc_postcode} onChange={set('dc_postcode')} error={errors.dc_postcode} />
                  <NSField label="Logradouro"  req placeholder="Rodovia SC-486" value={form.dc_street} onChange={set('dc_street')} error={errors.dc_street} />
                  <NSField label="Nº"  placeholder="3200" value={form.dc_number} onChange={set('dc_number')} error={errors.dc_number} />
                  <NSField label="Complemento"  placeholder="Galpão 3" value={form.dc_complement} onChange={set('dc_complement')} error={errors.dc_complement} />
                  <NSField label="Bairro"  placeholder="Distrito Industrial" value={form.dc_neighborhood} onChange={set('dc_neighborhood')} error={errors.dc_neighborhood} />
                  <NSField label="Cidade"  req placeholder="Brusque" value={form.dc_city} onChange={set('dc_city')} error={errors.dc_city} />
                  <NSField label="UF"  placeholder="SC" value={form.dc_state} onChange={set('dc_state')} error={errors.dc_state} />
                </div>
              </>
            )}

            {/* STEP 5 — Revisão */}
            {step === 5 && (
              <>
                {/* Preview hero */}
                <div style={{ display:'flex', alignItems:'center', gap:16, padding:18, background:'var(--bg-subtle)', borderRadius:12, marginBottom:24 }}>
                  <div style={{ width:52, height:52, borderRadius:12, background:'var(--brand-navy)', color:'white', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:700, flexShrink:0 }}>
                    {form.name.split(' ').filter(w=>w.length>1).slice(0,2).map(w=>w[0]).join('').toUpperCase()||'?'}
                  </div>
                  <div>
                    <div style={{ fontWeight:700, fontSize:18 }}>{form.name}</div>
                    <div style={{ fontSize:13, color:'var(--text-muted)', fontFamily:'JetBrains Mono, monospace' }}>{domainPreview || `${form.domain}${domainSuffix}`}</div>
                    <div style={{ fontSize:12, color:'var(--text-muted)' }}>{form.fiscal_name}</div>
                  </div>
                  <div style={{ marginLeft:'auto', textAlign:'right' }}>
                    <div style={{ fontWeight:700, fontSize:16 }}>R$ {plan.price}<span style={{ fontSize:12, color:'var(--text-muted)', fontWeight:400 }}>/mês</span></div>
                    <div style={{ fontSize:12, color:'var(--text-muted)' }}>Plano {plan.name}</div>
                  </div>
                </div>

                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
                  {[
                    { title:'Acesso', rows:[['E-mail admin',form.email,true],['Senha','••••••••']] },
                    { title:'Loja', rows:[['CNPJ',form.cnpj,true],['Telefone',form.phone],['Endereço',`${form.street}, ${form.number} — ${form.city}/${form.state}`]] },
                    { title:'Notificações', rows:[['Remetente',form.notification_sender_name||form.name],['E-mail',form.notification_sender_email||form.email,true]] },
                    { title:'Centro de Distribuição', rows:[['Nome',form.dc_name],['Tipo',form.dc_type.replace(/_/g,' ')],['Endereço',`${form.dc_street}, ${form.dc_number} — ${form.dc_city}/${form.dc_state}`]] },
                  ].map(block => (
                    <div key={block.title} style={{ background:'var(--bg-subtle)', borderRadius:10, padding:14 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:'var(--text-muted)', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:10 }}>{block.title}</div>
                      {block.rows.map(([label,value,mono]) => (
                        <div key={label} style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'4px 0', borderBottom:'1px solid var(--border)' }}>
                          <span style={{ color:'var(--text-muted)' }}>{label}</span>
                          <span style={{ fontWeight:500, fontFamily:mono?'JetBrains Mono, monospace':'inherit', fontSize:mono?12:13, maxWidth:200, textAlign:'right', wordBreak:'break-all' }}>{value}</span>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                <div style={{ background:'var(--info-bg)', color:'var(--info)', borderRadius:8, padding:'12px 14px', fontSize:13, display:'flex', gap:10 }}>
                  <Icon name="alert" size={15} style={{ flexShrink:0, marginTop:1 }} />
                  <span>Ao confirmar, será feita uma chamada <span style={{ fontFamily:'JetBrains Mono, monospace', fontSize:12 }}>POST /api/v1/super-admin/tenants/</span>. O lojista receberá as credenciais por e-mail.</span>
                </div>
              </>
            )}
          </div>

          {/* Ações */}
          <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:16, alignItems:'center' }}>
            {step > 1 && !loading && (
              <button className="btn ghost" onClick={() => setStep(s => s - 1)} style={{ marginRight:'auto' }}>
                <Icon name="chevronRight" size={12} style={{ transform:'rotate(180deg)' }} />Voltar
              </button>
            )}
            <button className="btn ghost" onClick={() => onNav({ page:'lojas' })} disabled={loading}>Cancelar</button>
            {step < STEPS.length
              ? <button className="btn primary" onClick={handleNext} style={{ minWidth:130, justifyContent:'center' }}>
                  Próximo <Icon name="chevronRight" size={12} />
                </button>
              : <button className="btn primary" onClick={handleCreate} disabled={loading} style={{ minWidth:160, justifyContent:'center' }}>
                  {loading
                    ? <><span style={{ display:'inline-block', width:14, height:14, border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'white', borderRadius:'50%', animation:'spin 0.6s linear infinite', marginRight:6 }} />Criando tenant…</>
                    : <><Icon name="store" size={14} />Criar tenant</>
                  }
                </button>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export {
  NewStorePage,
};
